import {
  Book,
  Contact,
  ContactDataSourceSchema,
  ContactRoleProducerSchema,
} from '@eigtech/contacts-types'
import { formatUsPhoneToHtml } from '@eigtech/contacts-util'
import { useGetAssignmentsForAssignable } from '@eigtech/ui-shared-assignments'
import {
  AugmentedContact,
  augmentContact,
  getBookEntityCorn,
  getContactAssignmentForEntity,
  getContactAssignmentLabel,
  getContactRoleLabel,
  isEntityBook,
} from '@eigtech/ui-shared-contacts'
import {
  ACTION_MENU_ID,
  DataGrid,
  DataGridBooleanFilter,
  TableState,
  createColumnHelper,
} from '@eigtech/ui-shared-data-grid'
import {
  Avatar,
  Badge,
  DataSource,
  FancyLink,
  HStack,
  Tag,
  Text,
  Wrap,
  WrapItem,
  formatArrayAsStringList,
} from '@eigtech/ui-shared-dave'
import { useMemo, useRef } from 'react'
import { ContactActionMenu, ContactActionMenuProps } from './ContactActionMenu'

export type ContactsTableProps = {
  book?: Book
  contacts?: Contact[]
  isError?: boolean
  isPending?: boolean
  isFetching?: boolean
  contactActionMenuItems?: ContactActionMenuProps['menuItems']
  enableFilters?: boolean
}

export function ContactsTable({
  book,
  contacts,
  isError,
  isFetching,
  isPending,
  contactActionMenuItems,
  enableFilters,
}: ContactsTableProps) {
  const contactActionMenuItemsRef = useRef(contactActionMenuItems)

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: ACTION_MENU_ID,
        maxSize: 80,
        cell: (cell) => (
          <ContactActionMenu
            book={book}
            contact={cell.row.original}
            menuItems={contactActionMenuItemsRef.current}
          />
        ),
      }),
      ...(!!book && isEntityBook(book)
        ? [
            columnHelper.display({
              id: 'primaryContact',
              header: 'Assignee',
              cell: ({ row }) => <AssigneeColumn book={book} contact={row.original} />,
            }),
          ]
        : []),
      columnHelper.accessor('contactName', {
        id: 'contactName',
        header: 'Name',
        cell: ({ getValue, row }) => (
          <HStack>
            <Text>{getValue()}</Text>
            {!!row.original.metadata.inactive && <Badge colorScheme="yellow">INACTIVE</Badge>}
          </HStack>
        ),
      }),
      columnHelper.accessor('rolesString', {
        id: 'rolesString',
        enableHiding: false,
      }),
      columnHelper.accessor('labeledRoles', {
        id: 'roles',
        header: 'Roles',
        enableSorting: false,
        filterFn: 'arrIncludesSome',
        meta: {
          arrayFilterOptions: ContactRoleProducerSchema.options.map((role) => ({
            label: getContactRoleLabel(role),
            value: getContactRoleLabel(role),
          })),
          filterLabel: (value) =>
            `includes ${formatArrayAsStringList(value, {
              type: 'disjunction',
            })}`,
        },
        cell: (cell) => (
          <Wrap>
            {cell.getValue().map((role) => (
              <WrapItem key={role}>
                <Tag>{role}</Tag>
              </WrapItem>
            ))}
          </Wrap>
        ),
      }),
      columnHelper.accessor('preferredContact', {
        id: 'preferredContact',
        header: 'Preferred Contact',
        cell: (cell) => {
          const value = cell.getValue()

          if (value.includes('@')) {
            return (
              <FancyLink href={`mailto:${value}`} iconPosition="left">
                {value}
              </FancyLink>
            )
          }

          if (value.includes('-')) {
            return (
              <FancyLink href={`tel:${formatUsPhoneToHtml(value)}`} iconPosition="left">
                {value}
              </FancyLink>
            )
          }

          return value
        },
      }),
      columnHelper.accessor('metadata.dataSource', {
        id: 'dataSource',
        header: 'Data Source',
        filterFn: 'arrIncludes',
        meta: {
          arrayFilterOptions: ContactDataSourceSchema.options,
        },
        cell: (cell) => <DataSource>{cell.getValue()}</DataSource>,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
      }),
      ...(book?.metadata.type === 'global'
        ? [
            columnHelper.accessor('metadata.licenseNumber', {
              id: 'licenseNumber',
              header: 'License',
            }),
            columnHelper.accessor('picture', {
              id: 'picture',
              header: 'Picture',
              maxSize: 50,
              cell: ({ getValue, row }) => (
                <Avatar name={row.original.contactName} size="sm" src={getValue() ?? undefined} />
              ),
              filterFn(row, columnId, filterValue) {
                const value = row.getValue<string>(columnId)
                const isUploadedPicture = !!value && !value.includes('s.gravatar.com')

                if (filterValue === true) {
                  return isUploadedPicture
                }

                if (filterValue === false) {
                  return !isUploadedPicture
                }

                return false
              },
              meta: {
                filterLabel: (value) => (value ? 'is uploaded' : 'is default (gravatar)'),
                FilterComponent: ({ column }) => <DataGridBooleanFilter column={column} />,
              },
            }),
          ]
        : []),
      columnHelper.accessor('metadata.inactive', {
        id: 'inactive',
        enableHiding: false,
        meta: {
          FilterComponent: ({ column }) => <DataGridBooleanFilter column={column} />,
        },
      }),
    ],

    [book]
  )
  const augmentedContacts = useMemo(() => (contacts ?? []).map(augmentContact), [contacts])

  return (
    <DataGrid
      columns={columns}
      data={augmentedContacts}
      enableFilters={enableFilters}
      initialState={state}
      isError={isError}
      isFetching={isFetching}
      isPending={isPending}
    />
  )
}

const state: Partial<TableState> = {
  columnVisibility: { inactive: false, rolesString: false },
}

function AssigneeColumn({ book, contact }: { book: Book; contact: AugmentedContact }) {
  const entityCorn = getBookEntityCorn(book)

  const { data: assignments } = useGetAssignmentsForAssignable(entityCorn)

  const assigned = getContactAssignmentForEntity({
    assignments,
    contactId: contact.contactId,
  })

  if (assigned) {
    const processedAssigned = assigned?.map((a) =>
      getContactAssignmentLabel(a.assignable.assigneeRelationship)
    )
    return (
      <Wrap>
        {processedAssigned.map((assignment) => (
          <WrapItem key={assignment}>
            <Tag colorScheme="cyan">{assignment}</Tag>
          </WrapItem>
        ))}
      </Wrap>
    )
  }

  return <></>
}

const columnHelper = createColumnHelper<AugmentedContact>()
